import React, { Fragment, useEffect, useState } from 'react'

import {
  AlignItems,
  Box,
  Display,
  DisplayPosition,
  Heading1,
  JustifyContent,
  Space,
  TextAlign,
  ZIndex,
} from '@gousto-internal/citrus-react'
import Loading from 'Loading'
import { useDispatch, useSelector } from 'react-redux'

import { useMarketingPricingConfig } from '@library/api-pricing'

import { getAvailableBoxSizes } from 'actions/basket'
import { menuLoadBoxPrices } from 'actions/menu'
import { discountBannerBoxPrices } from 'actions/trackingKeys'
import { boxSizes as boxSize, getHeroDetails } from 'routes/BoxPrices/boxPricesConfig'
import { getAvailableNumPortions } from 'selectors/basket'
import { locationQuery } from 'selectors/routing'

import { PromoBanner } from '../Home/PromoBanner/PromoBanner'
import { BoxPricesContent } from './BoxPricesContent'
import { BoxPricesWithAllPortions } from './BoxPricesWithAllPortions/BoxPricesWithAllPortions'
import { NumPersonsToBoxDescriptors } from './boxPricesModels'
import { getMenuBoxPricesReceive, getNumPersonsToBoxDescriptors } from './boxPricesSelectors'

const BoxPricesComponent = () => {
  const dispatch = useDispatch()
  const pricingConfig = useMarketingPricingConfig()
  const { pmaxPortions, pmaxRecipes } = useSelector(locationQuery)

  // Redux selectors (fallback when config is null)
  const loading = useSelector(getMenuBoxPricesReceive)
  const reduxAvailableBoxSizes = useSelector(getAvailableNumPortions)
  const reduxNumPersonsToBoxDescriptors = useSelector(
    getNumPersonsToBoxDescriptors,
  ) as NumPersonsToBoxDescriptors

  const [pmaxApplied, setPmaxApplied] = useState(false)
  const [activeNumPortion, setActiveNumPortion] = useState(2)
  const [activeRecipes, setActiveRecipes] = useState(boxSize[2].length - 1)

  // Use config if available, otherwise fallback to Redux
  const numPersonsToBoxDescriptors =
    pricingConfig?.numPersonsToBoxDescriptors || reduxNumPersonsToBoxDescriptors

  // Derive available box sizes from config or Redux
  const availableBoxSizes = pricingConfig?.availableBoxSizes || reduxAvailableBoxSizes

  const handleActivePortionSize = (portionSize: number) => {
    setActiveNumPortion(portionSize)
    setActiveRecipes(boxSize[portionSize].length - 1)
  }

  // Fetch Redux data if config is not available
  useEffect(() => {
    if (!pricingConfig) {
      if (!reduxAvailableBoxSizes?.length) {
        dispatch(getAvailableBoxSizes())
      }
      dispatch(menuLoadBoxPrices())
    }
  }, [dispatch, pricingConfig, reduxAvailableBoxSizes?.length])

  // Handle pmax URL parameters
  useEffect(() => {
    if (availableBoxSizes?.length && pmaxPortions && pmaxRecipes && !pmaxApplied) {
      const pmaxPortionsNum = Number(pmaxPortions)
      if (
        availableBoxSizes.includes(pmaxPortionsNum) &&
        boxSize[pmaxPortions]?.includes(pmaxRecipes)
      ) {
        setActiveNumPortion(pmaxPortionsNum)
        setActiveRecipes(boxSize[pmaxPortions].indexOf(pmaxRecipes))
        setPmaxApplied(true)
      }
    }
  }, [pmaxRecipes, pmaxPortions, availableBoxSizes, pmaxApplied])

  const conditionalLoadingStyle = loading ? { filter: 'blur(5px)' } : {}

  return (
    <Fragment>
      {loading && (
        <Box
          display={Display.Flex}
          position={DisplayPosition.Fixed}
          top="0px"
          zIndex={ZIndex.Modal}
          width="100%"
          height="100%"
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          data-testid="boxPricesComponentLoading"
        >
          <Loading />
        </Box>
      )}
      <Box style={conditionalLoadingStyle} data-testid="boxPricesComponentLoaded">
        <PromoBanner trackingEventName={discountBannerBoxPrices} />
        <Space size={[6, 8]} direction="vertical" />
        <Heading1 textAlign={TextAlign.Center}>{getHeroDetails().header}</Heading1>
        <Space size={[6, 8]} direction="vertical" />
        {!loading && (
          <Box
            display={Display.Flex}
            justifyContent={JustifyContent.Center}
            gap={8}
            data-testid="boxPricesWithAllPortions"
          >
            {availableBoxSizes?.length > 0 && (
              <BoxPricesWithAllPortions
                boxInfo={numPersonsToBoxDescriptors}
                onClickPortionSize={handleActivePortionSize}
                activeNumPortion={activeNumPortion}
                portionSizes={availableBoxSizes}
                activeRecipes={activeRecipes}
                setActiveRecipes={setActiveRecipes}
              />
            )}
          </Box>
        )}
        <Space size={[8, 18]} />
        <BoxPricesContent activeNumPortion={activeNumPortion} />
      </Box>
    </Fragment>
  )
}

export { BoxPricesComponent }
